// =============================================================================
// deliveriesSync.js — inbound deliveries dual-write (faithful v1 rebuild).
//
// Best-effort: when the kitchen photo-confirms an inbound stock arrival, this
// records a FAITHFUL Supabase copy of what the tile captures — the photo bytes
// into a PRIVATE bucket (0061) AND an evidence row into deliveries_inbound
// (0062): the item, the photo + its capture metadata, note, and the stock/custom
// kind. The STATE change (delivered / back_in_stock) still rides stock_activity
// via shadowKitchenWrite — untouched. Sibling contract to stockSync/wastageSync:
// gated by dualRunEnabled (live kitchen host or ?dualrun=on) and self-swallowing,
// so it can NEVER block or throw into the kitchen path.
//
// Lane BP (13 Sep 2026): on a host in Lane BE's STOCK_WRITE_DROP_HOSTS (ships EMPTY)
// the kitchen stops writing inbound deliveries to Google; recordInboundDelivery is
// then AWAITED (strictPhoto) and its honest ok/!ok verdict blocks the tile. It still
// never throws. See planInboundSubmit below — the pure OFF/ON decision.
//
// Lane CF (14 Sep 2026, closes BP-5 + BP-7):
//   BP-5 — the Confirm-delivery modal (boh-kitchen.jsx DeliveryConfirmModal) never
//     called recordInboundDelivery at all; only the Inbound Deliveries tile did. The
//     modal now calls it too, on the write-drop ON path, right after its own stock
//     event lands — see planModalEvidence below (the modal's pure seam, mirroring
//     planInboundSubmit).
//   BP-7 — deliveries_inbound.qty (the column has existed since 0062) was never
//     written by any caller: recordInboundDelivery's own params never included qty,
//     so a caller that already passed one (the tile's OFF-path shadow call did) had
//     it silently dropped before the upsert. Fixed by threading qty through to a new
//     pure buildInboundDeliveryRow — see below.
//
// Scope = JJ's shipped v1 ("just a faithful rebuild"): no purchase-order v2, no
// damaged/dispute flow. The photo + its evidence row IS the record.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, siteIdForKey, shadow, ensurePass, idem } from "./stockSync.js";
import { deliveryPhotoKey } from "./deliveryPhotoKey.js";
import { record as recordActivity } from "./activity.js";

const BUCKET = "delivery-photos";               // PRIVATE evidence copy (0061)
const PUBLIC_BUCKET = "delivery-photos-public"; // PUBLIC fast display copy (0701)
const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
const num = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v));

// The kitchen tile fires app action names; the stock event log (and this
// evidence table, and the CC label map) speak the event vocabulary. Map here so
// deliveries_inbound.action matches the documented domain (back_in_stock /
// delivered) — same mapping stockSync.recordKitchen uses.
const EVENT_FOR = { stockDelivered: "delivered", customDeliveryReceived: "delivered", backInStock: "back_in_stock" };

// --- Lane BP (13 Sep 2026): the inbound write-drop plan — PURE --------------------
// The kitchen has ONE write-drop gate: Lane BE's STOCK_WRITE_DROP_HOSTS, read through
// stockSync.stockWriteDropEnabled() (ships EMPTY; ?stockwritedrop=off always wins).
// Both inbound photo flows in boh-kitchen.jsx — the Confirm-delivery modal and the
// Inbound Deliveries tile — hand that verdict to THIS function and follow its plan,
// so the whole OFF/ON truth table is unit-tested with no window:
//   OFF (today, byte-identical): a missed Storage upload may fall back to Apps Script
//        -> Drive; the Sheet POST (uploadDeliveryPhoto / receiveInboundBatch /
//        the per-event kitchen action) fires; the Supabase writes stay fire-and-forget
//        shadows that swallow their own errors.
//   ON:  no Drive fallback and no Sheet POST; the Storage photo, the evidence row
//        (recordInboundDelivery, strictPhoto) and the stock event (postKitchenAction
//        -> stockWriteAuthoritative) are AWAITED, and any failure blocks the "done"
//        UI honestly — nothing claims saved that is not in Supabase.
// Only a literal `true` turns the drop on: a truthy string can never arm it.
export function planInboundSubmit({ dropOn = false } = {}) {
  const on = dropOn === true;
  return { driveFallback: !on, sheetPost: !on, supabaseAuthoritative: on };
}

// --- Lane CF (14 Sep 2026): should THIS fired kitchen event also record a
// deliveries_inbound evidence row? PURE — no network, no React, no window. Closes
// BP-5 (the modal recorded no evidence at all). The modal calls this once per
// postKitchenAction it fires (one per eventsToFire/propagatedEvents entry), right
// after that call resolves, mirroring the Inbound Deliveries tile's own per-line
// sequence (Lane BP): the stock event lands FIRST, the evidence row SECOND, and
// either failing fails that pair. Two independent inputs, both required:
//   supabaseAuthoritative — the SAME planInboundSubmit() verdict the caller already
//     computed once for the whole submit (never re-derived per event, so a submit
//     can't half-drop mid-flight if the gate flips between two awaits);
//   stockEventOk — did THIS event's own postKitchenAction call land. A failed stock
//     event never gets an evidence attempt (nothing to attach evidence to), exactly
//     like the tile's `if (!res || res.ok === false) return {...}` short-circuit.
// On the OFF path (supabaseAuthoritative:false) this is false unconditionally, for
// every stockEventOk value — the modal's OFF path never calls recordInboundDelivery,
// byte-identical to today, with no dependency on network timing to prove it.
export function planModalEvidence({ supabaseAuthoritative = false, stockEventOk = false } = {}) {
  return { recordEvidence: supabaseAuthoritative === true && stockEventOk === true };
}

// Cheap deterministic string hash (djb2) — used to key a photo by its CONTENT
// when it has no EXIF capture time, so a retry of the SAME photo re-uses the SAME
// key (dedupes) while two genuinely different deliveries differ.
function hashStr(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

// A per-photo token that is STABLE across a retry of the same photo: the EXIF
// capture time if present, else a hash of the photo bytes. (A random fallback
// only if there is somehow no photo — inbound always has one.)
function photoUniq(capturedAt, base64) {
  if (capturedAt != null && String(capturedAt).trim() !== "") return String(capturedAt);
  if (base64) return "p" + hashStr(base64);
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Date.now() + "-" + Math.random().toString(36).slice(2, 8);
}

function base64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// --- activity log line ---------------------------------------------------------
// A photo-confirmed inbound delivery is also a "who did what, where, when" event
// for the estate activity feed (activity.js → activity_record, 0042/0076).
// Fire-and-forget, no PIN (the database resolves the staff id from the login pass
// ensurePass minted on the shared connection), never breaks the kitchen path. It
// writes on `supabase` — the same connection the evidence row was written and
// signed on — and fires only after that shadow write succeeded.
//
// Pure, exported mapper + `_activitySink` seam so the wiring is unit-tested
// without a network round-trip.
export function deliveryActivityLine(item) {
  if (!item) return null;
  return { action: "inboundDelivery", kind: "action", target: item };
}

export const _activitySink = { record: recordActivity };

export function logDeliveryActivity(siteKey, item) {
  const line = deliveryActivityLine(item);
  if (!line) return null;
  try {
    return _activitySink.record({ siteKey, ...line, client: supabase });
  } catch (e) {
    console.warn("[deliveries-sync] activity line threw (delivery write unaffected):", e?.message || e);
    return null;
  }
}

// Best-effort upload of one photo to the private bucket at a given key. Never
// throws; upsert:false so an existing key (same-photo retry) is a harmless no-op.
async function uploadPhotoAtKey(key, base64, mime) {
  try {
    const { error } = await supabase.storage.from(BUCKET).upload(key, base64ToBytes(base64), { contentType: mime || "image/jpeg", upsert: false });
    if (error && !/exists|duplicate/i.test(error.message || "")) { console.warn("[deliveries-sync] photo upload failed:", error.message || error); return { ok: false }; }
    return { ok: true, key };
  } catch (e) { console.warn("[deliveries-sync] photo upload threw:", e?.message || e); return { ok: false }; }
}

// Pure row-builder — the exact upsert payload for one deliveries_inbound row.
// Split out (Lane CF, 14 Sep 2026, BP-7) so the SHAPE is unit-tested with no
// network — in particular, that `qty` survives (previously silently dropped:
// recordInboundDelivery's own params never included it) and that a genuine 0
// is kept, never coalesced away like a missing value. Mirrors the split
// stockSync.js already has (buildStockEntry / stockWriteAuthoritative).
export function buildInboundDeliveryRow({ siteId, action, item, kind, note, photoUrl, photoKey, capturedAt, exifPresent, captureSource, gpsLat, gpsLng, qty, day, idemKey }) {
  return {
    site_id: siteId,
    item_name: item,
    kind: kind === "custom" ? "custom" : "stock",
    action,
    note: note || null,
    photo_url: photoUrl || null,
    photo_paths: photoKey ? [photoKey] : null,
    captured_at: capturedAt || null,
    exif_present: typeof exifPresent === "boolean" ? exifPresent : null,
    capture_source: captureSource || null,
    gps_lat: num(gpsLat),
    gps_lng: num(gpsLng),
    qty: num(qty),
    event_date: day,
    idem_key: idemKey,
  };
}

// One call per photo-confirmed inbound delivery. Uploads the photo AND writes the
// evidence row, aligned by a shared per-delivery token that survives a retry.
// base64 = raw base64 (no data: prefix). staff_id is stamped by the DB from the
// login pass — never a PIN. Never throws.
// Lane BP: `strictPhoto` (default false = today's shadow) is set by the write-drop
// ON path, where this call is AWAITED and its verdict blocks the tile: a failed
// private evidence upload then returns ok:false BEFORE any row is written, so the
// record can never claim a photo it does not hold.
// Lane CF (14 Sep 2026, BP-7): `qty` — optional, the tile's cart stepper collects
// it, the modal has no qty UI and omits it (stays null, same as every other path
// today). Threaded straight into buildInboundDeliveryRow; not part of idem_key (a
// retry of the SAME confirm always carries the SAME qty in practice, and the table
// is append-only evidence, not a corrected record — same "first write wins on a
// collapse" shape note/photo already had).
export async function recordInboundDelivery({ siteKey, action, item, kind, staffPin, note, photoUrl, base64, mime, capturedAt, gpsLat, gpsLng, exifPresent, captureSource, qty, day = londonToday(), strictPhoto = false }) {
  try {
    if (!dualRunEnabled()) return { ok: true, skipped: true };
    if (!siteKey || !item) return { ok: false, error: "missing site/item" };
    const pass = await ensurePass(staffPin, siteKey);
    if (!pass?.ok) { console.warn("[deliveries-sync] no pass — inbound shadow skipped"); return { ok: false, error: "no pass" }; }
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };

    const ev = EVENT_FOR[action] || action || null;   // stored + keyed on the event name
    const uniq = photoUniq(capturedAt, base64);
    let photoKey = null;
    if (base64) {
      photoKey = deliveryPhotoKey(siteKey, ev, item, day, mime, uniq);
      const up = await uploadPhotoAtKey(photoKey, base64, mime); // shadow: best-effort, the row still records the key + URL
      if (strictPhoto && !(up && up.ok)) return { ok: false, error: "evidence photo upload failed" };   // ON path: no evidence, no row
    }

    // ON CONFLICT (idem_key) DO NOTHING: a retried confirm re-uses the same
    // (site, event, item, day, photo) -> same idem -> collapses to one row.
    const row = buildInboundDeliveryRow({
      siteId, action: ev, item, kind, note, photoUrl, photoKey,
      capturedAt, exifPresent, captureSource, gpsLat, gpsLng, qty, day,
      idemKey: idem(siteKey, "inbound", ev, item, day, uniq),
    });
    const res = await shadow("inbound delivery " + item, () =>
      supabase.from("deliveries_inbound").upsert(row, { onConflict: "idem_key", ignoreDuplicates: true }));
    if (res.ok) logDeliveryActivity(siteKey, item);
    return res;
  } catch (e) {
    console.warn("[deliveries-sync] recordInboundDelivery threw:", e?.message || e);
    return { ok: false, error: e };
  }
}

// FAST primary-photo upload — straight to the PUBLIC bucket (sub-second) instead
// of Apps Script -> Drive (~8s). Returns a PERMANENT public URL for the record,
// or null on ANY failure so the caller falls back to the Drive upload. Reuses the
// same FOH pass (app_session claim) the private evidence upload already relies on.
// base64 = raw base64 (no data: prefix). Never throws.
export async function uploadDeliveryPhotoFast({ siteKey, item, staffPin, base64, mime, capturedAt, day = londonToday() }) {
  try {
    if (!base64) return null;
    const pass = await ensurePass(staffPin, siteKey);
    if (!pass?.ok) { console.warn("[deliveries-sync] fast upload — no pass, falling back to Drive"); return null; }
    const ext = ((String(mime || "image/jpeg").split("/")[1] || "jpg").replace("jpeg", "jpg").replace(/[^a-z0-9]/gi, "")) || "jpg";
    const safeItem = (String(item || "delivery").replace(/[^a-z0-9_-]+/gi, "_").slice(0, 40)) || "delivery";
    const uniq = photoUniq(capturedAt, base64);
    const key = `${siteKey}/${day}/${safeItem}_${uniq}.${ext}`;
    const { error } = await supabase.storage.from(PUBLIC_BUCKET).upload(key, base64ToBytes(base64), { contentType: mime || "image/jpeg", upsert: false });
    if (error && !/exists|duplicate/i.test(error.message || "")) { console.warn("[deliveries-sync] fast upload failed:", error.message || error); return null; }
    const { data } = supabase.storage.from(PUBLIC_BUCKET).getPublicUrl(key);
    return (data && data.publicUrl) || null;
  } catch (e) {
    console.warn("[deliveries-sync] fast upload threw:", e?.message || e);
    return null;
  }
}
